/**
 * CompareSlider - Vanilla DOM overlay for before/after splat comparison
 *
 * Creates a draggable divider line with handle that splits the viewport.
 * Supports vertical and horizontal orientation, keyboard control, and double-click snap.
 */
import type { CompareConfig } from '../types';

export interface CompareSliderOptions {
    container: HTMLElement;
    config: CompareConfig;
    onPositionChange: (position: number) => void;
    theme?: {
        compareLineColor?: string;
        compareHandleBg?: string;
        compareHandleBorder?: string;
        compareLabelBg?: string;
        compareLabelColor?: string;
        compareLabelFontSize?: string;
    };
}

function createArrowSVG(orientation: 'vertical' | 'horizontal', color = 'white'): SVGSVGElement {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'none');

    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    const attrs = { stroke: color, 'stroke-width': '1.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
    for (const [k, v] of Object.entries(attrs)) {
        path1.setAttribute(k, v);
        path2.setAttribute(k, v);
    }

    if (orientation === 'vertical') {
        path1.setAttribute('d', 'M6 10L2 10M2 10L4.5 7.5M2 10L4.5 12.5');
        path2.setAttribute('d', 'M14 10L18 10M18 10L15.5 7.5M18 10L15.5 12.5');
    } else {
        path1.setAttribute('d', 'M10 6L10 2M10 2L7.5 4.5M10 2L12.5 4.5');
        path2.setAttribute('d', 'M10 14L10 18M10 18L7.5 15.5M10 18L12.5 15.5');
    }

    svg.appendChild(path1);
    svg.appendChild(path2);
    return svg;
}

export class CompareSlider {
    private container: HTMLElement;
    private overlay: HTMLElement;
    private lineA: HTMLElement; // line segment before the handle
    private lineB: HTMLElement; // line segment after the handle
    private handle: HTMLElement;
    private beforeLabel: HTMLElement;
    private afterLabel: HTMLElement;
    private position: number; // 0-1
    private isDragging = false;
    private orientation: 'vertical' | 'horizontal';
    private onPositionChange: (position: number) => void;
    private labelFadeTimeout: ReturnType<typeof setTimeout> | null = null;
    private config: CompareConfig;
    private snapPositions = [0, 0.5, 1];
    private lastSnapIndex = 1; // Start at 0.5
    private destroyed = false;
    private _resizeObserver: ResizeObserver | null = null;

    // Bound handlers for cleanup
    private _onPointerDown: (e: PointerEvent) => void;
    private _onPointerMove: (e: PointerEvent) => void;
    private _onPointerUp: () => void;
    private _onKeyDown: (e: KeyboardEvent) => void;
    private _onDblClick: () => void;

    constructor(options: CompareSliderOptions) {
        this.container = options.container;
        this.config = options.config;
        this.onPositionChange = options.onPositionChange;
        this.orientation = options.config.orientation || 'vertical';
        this.position = (options.config.initialPosition ?? 50) / 100;

        const theme = options.theme || {};

        // Create overlay container
        this.overlay = document.createElement('div');
        this.overlay.className = 'ss-compare-overlay';
        Object.assign(this.overlay.style, {
            position: 'absolute',
            inset: '0',
            pointerEvents: 'none',
            zIndex: '10',
            overflow: 'hidden',
        });

        // Divider line (two segments that break around the handle)
        const lineColor = theme.compareLineColor || options.config.lineColor || '#ffffff';
        const lineOpacity = options.config.lineOpacity ?? 0.6;
        const handleColor = options.config.handleColor || lineColor;
        const lineBaseStyle = {
            position: 'absolute',
            pointerEvents: 'none',
            boxShadow: '0 0 4px rgba(0,0,0,0.4)',
        };
        this.lineA = document.createElement('div');
        this.lineA.className = 'ss-compare-line';
        Object.assign(this.lineA.style, lineBaseStyle);
        this.lineB = document.createElement('div');
        this.lineB.className = 'ss-compare-line';
        Object.assign(this.lineB.style, lineBaseStyle);
        this._applyLineColor(lineColor, lineOpacity);

        // Drag handle — separate handleColor for border/icon
        this.handle = document.createElement('div');
        this.handle.className = 'ss-compare-handle';
        this.handle.tabIndex = 0;
        this.handle.setAttribute('role', 'slider');
        this.handle.setAttribute('aria-label', 'Compare slider');
        this.handle.setAttribute('aria-valuemin', '0');
        this.handle.setAttribute('aria-valuemax', '100');
        Object.assign(this.handle.style, {
            position: 'absolute',
            cursor: this.orientation === 'vertical' ? 'ew-resize' : 'ns-resize',
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translate(-50%, -50%)',
            transition: 'box-shadow 0.15s ease',
            outline: 'none',
            touchAction: 'none',
            boxSizing: 'border-box',
        });

        // Apply initial handle style (reads handleColor/handleOpacity from config)
        this.updateHandleStyle(options.config.handleStyle || 'arrows');

        // Labels
        const labelBg = theme.compareLabelBg || 'rgba(0,0,0,0.5)';
        const labelColor = theme.compareLabelColor || '#ffffff';
        const labelFontSize = theme.compareLabelFontSize || '13px';

        this.beforeLabel = this._createLabel(
            options.config.beforeLabel || 'Before',
            labelBg, labelColor, labelFontSize
        );
        this.afterLabel = this._createLabel(
            options.config.afterLabel || 'After',
            labelBg, labelColor, labelFontSize
        );

        // Assemble
        this.overlay.appendChild(this.lineA);
        this.overlay.appendChild(this.lineB);
        this.overlay.appendChild(this.handle);
        this.overlay.appendChild(this.beforeLabel);
        this.overlay.appendChild(this.afterLabel);
        if (options.config.showLabels === false) {
            this.beforeLabel.style.display = 'none';
            this.afterLabel.style.display = 'none';
        }
        this.container.appendChild(this.overlay);

        // Set initial position
        this._updateLayout();

        // Bind event handlers
        this._onPointerDown = this._handlePointerDown.bind(this);
        this._onPointerMove = this._handlePointerMove.bind(this);
        this._onPointerUp = this._handlePointerUp.bind(this);
        this._onKeyDown = this._handleKeyDown.bind(this);
        this._onDblClick = this._handleDblClick.bind(this);

        this.handle.addEventListener('pointerdown', this._onPointerDown);
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);

        if (options.config.enableKeyboard !== false) {
            this.handle.addEventListener('keydown', this._onKeyDown);
        }
        if (options.config.enableSnap !== false) {
            this.handle.addEventListener('dblclick', this._onDblClick);
        }

        // Focus ring
        this.handle.addEventListener('focus', () => {
            this.handle.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
        });
        this.handle.addEventListener('blur', () => {
            this.handle.style.boxShadow = '';
        });

        // Recalculate layout on container resize
        this._resizeObserver = new ResizeObserver(() => {
            if (!this.destroyed) this._updateLayout();
        });
        this._resizeObserver.observe(this.container);

        // Start label fade timer
        this._resetLabelFade();
    }

    private _createLabel(text: string, bg: string, color: string, fontSize: string): HTMLElement {
        const label = document.createElement('div');
        label.className = 'ss-compare-label';
        label.textContent = text;
        Object.assign(label.style, {
            position: 'absolute',
            top: '20px',
            pointerEvents: 'none',
            background: bg,
            color: color,
            fontSize: fontSize,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            padding: '4px 12px',
            borderRadius: '12px',
            whiteSpace: 'nowrap',
            transition: 'opacity 0.3s ease',
            userSelect: 'none',
        });
        return label;
    }

    private static _hexToRgba(color: string, opacity: number): string {
        const hex = color.replace('#', '');
        if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
            return `rgba(255,255,255,${opacity})`;
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r},${g},${b},${opacity})`;
    }

    private _applyLineColor(color: string, opacity: number) {
        const rgba = CompareSlider._hexToRgba(color, opacity);
        this.lineA.style.background = rgba;
        this.lineB.style.background = rgba;
    }

    private _updateLayout() {
        const pct = `${this.position * 100}%`;
        // Use actual handle size so the line gap adjusts for circle (28px) vs arrows (40px)
        const handleW = this.handle.offsetWidth || 40;
        const handleH = this.handle.offsetHeight || 40;
        const HALF_HANDLE = (this.orientation === 'vertical' ? handleH : handleW) / 2;

        if (this.orientation === 'vertical') {
            const containerH = this.container.clientHeight || 600;
            const containerW = this.container.clientWidth || 800;
            const handleY = containerH / 2; // handle is centered vertically

            // Line segment A: top of container to top of handle
            Object.assign(this.lineA.style, {
                left: pct, top: '0', width: '2px',
                height: `${Math.max(0, handleY - HALF_HANDLE)}px`,
            });
            // Line segment B: bottom of handle to bottom of container
            Object.assign(this.lineB.style, {
                left: pct, top: `${handleY + HALF_HANDLE}px`, width: '2px',
                height: `${Math.max(0, containerH - handleY - HALF_HANDLE)}px`,
            });

            this.handle.style.left = pct;
            this.handle.style.top = '50%';

            // Labels (guard for constructor order — labels created after handle)
            if (this.beforeLabel) {
                const labelOffset = 20;
                const dividerX = this.position * containerW;
                this.beforeLabel.style.left = `${Math.max(8, dividerX - labelOffset - 60)}px`;
                this.beforeLabel.style.right = '';
                this.beforeLabel.style.transform = '';
                this.afterLabel.style.left = `${Math.min(containerW - 8, dividerX + labelOffset)}px`;
                this.afterLabel.style.right = '';
                this.afterLabel.style.transform = '';
            }
        } else {
            const containerW = this.container.clientWidth || 800;
            const containerH = this.container.clientHeight || 600;
            const handleX = containerW / 2; // handle is centered horizontally

            // Line segment A: left of container to left of handle
            Object.assign(this.lineA.style, {
                top: pct, left: '0', height: '2px',
                width: `${Math.max(0, handleX - HALF_HANDLE)}px`,
            });
            // Line segment B: right of handle to right of container
            Object.assign(this.lineB.style, {
                top: pct, left: `${handleX + HALF_HANDLE}px`, height: '2px',
                width: `${Math.max(0, containerW - handleX - HALF_HANDLE)}px`,
            });

            this.handle.style.top = pct;
            this.handle.style.left = '50%';

            if (this.beforeLabel) {
                this.beforeLabel.style.left = '50%';
                this.beforeLabel.style.transform = 'translateX(-50%)';
                this.beforeLabel.style.top = `${Math.max(8, this.position * containerH - 40)}px`;
                this.afterLabel.style.left = '50%';
                this.afterLabel.style.transform = 'translateX(-50%)';
                this.afterLabel.style.top = `${Math.min(containerH - 30, this.position * containerH + 20)}px`;
            }
        }

        this.handle.setAttribute('aria-valuenow', String(Math.round(this.position * 100)));
    }

    private _handlePointerDown(e: PointerEvent) {
        e.preventDefault();
        e.stopPropagation();
        this.isDragging = true;
        this.handle.setPointerCapture(e.pointerId);
        this._showLabels();
    }

    private _handlePointerMove(e: PointerEvent) {
        if (!this.isDragging) return;
        e.preventDefault();

        const rect = this.container.getBoundingClientRect();
        let pos: number;
        if (this.orientation === 'vertical') {
            pos = (e.clientX - rect.left) / rect.width;
        } else {
            pos = (e.clientY - rect.top) / rect.height;
        }

        this._setPosition(Math.max(0, Math.min(1, pos)));
    }

    private _handlePointerUp() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this._resetLabelFade();
    }

    private _handleKeyDown(e: KeyboardEvent) {
        const step = 0.02;
        let newPos = this.position;

        if (this.orientation === 'vertical') {
            if (e.key === 'ArrowLeft') newPos -= step;
            else if (e.key === 'ArrowRight') newPos += step;
            else return;
        } else {
            if (e.key === 'ArrowUp') newPos -= step;
            else if (e.key === 'ArrowDown') newPos += step;
            else return;
        }

        e.preventDefault();
        this._setPosition(Math.max(0, Math.min(1, newPos)));
        this._showLabels();
        this._resetLabelFade();
    }

    private _handleDblClick() {
        this.lastSnapIndex = (this.lastSnapIndex + 1) % this.snapPositions.length;
        this._setPosition(this.snapPositions[this.lastSnapIndex]);
        this._showLabels();
        this._resetLabelFade();
    }

    private _setPosition(pos: number) {
        this.position = pos;
        this._updateLayout();
        this.onPositionChange(pos);
    }

    private _showLabels() {
        this.beforeLabel.style.opacity = '1';
        this.afterLabel.style.opacity = '1';
    }

    private _resetLabelFade() {
        if (this.labelFadeTimeout) {
            clearTimeout(this.labelFadeTimeout);
        }
        this._showLabels();
        this.labelFadeTimeout = setTimeout(() => {
            if (!this.destroyed) {
                this.beforeLabel.style.opacity = '0';
                this.afterLabel.style.opacity = '0';
            }
        }, 3000);
    }

    getPosition(): number {
        return this.position;
    }

    setPosition(pos: number) {
        this._setPosition(Math.max(0, Math.min(1, pos)));
    }

    setLabels(before: string, after: string) {
        this.beforeLabel.textContent = before;
        this.afterLabel.textContent = after;
    }

    setOrientation(orientation: 'vertical' | 'horizontal') {
        if (this.orientation === orientation) return;
        this.orientation = orientation;
        this.handle.style.cursor = orientation === 'vertical' ? 'ew-resize' : 'ns-resize';
        // Re-apply handle style for the new orientation
        this.updateHandleStyle(this.config.handleStyle || 'arrows');
        this._updateLayout();
    }

    updateLineStyle(color?: string, opacity?: number) {
        const c = color || this.config.lineColor || '#ffffff';
        const o = opacity ?? this.config.lineOpacity ?? 0.6;
        if (color !== undefined) this.config.lineColor = color;
        if (opacity !== undefined) this.config.lineOpacity = opacity;
        this._applyLineColor(c, o);
    }

    updateHandleColor(color: string, opacity?: number) {
        this.config.handleColor = color;
        if (opacity !== undefined) this.config.handleOpacity = opacity;
        // Re-apply handle style so border, icon, and fill all update
        this.updateHandleStyle(this.config.handleStyle || 'arrows');
    }

    private _handleRgba(): string {
        const color = this.config.handleColor || this.config.lineColor || '#ffffff';
        const opacity = this.config.handleOpacity ?? 0.6;
        return CompareSlider._hexToRgba(color, opacity);
    }

    updateHandleStyle(style: 'arrows' | 'circle' | 'minimal') {
        const hRgba = this._handleRgba();
        const hColor = this.config.handleColor || this.config.lineColor || '#ffffff';
        while (this.handle.firstChild) this.handle.removeChild(this.handle.firstChild);
        const handleBg = 'rgba(0,0,0,0.6)';
        if (style === 'arrows') {
            Object.assign(this.handle.style, {
                width: '40px', height: '40px', borderRadius: '50%',
                borderStyle: 'solid', borderWidth: '2px', borderColor: hRgba,
                background: handleBg,
            });
            this.handle.appendChild(createArrowSVG(this.orientation, hRgba));
        } else if (style === 'circle') {
            Object.assign(this.handle.style, {
                width: '28px', height: '28px', borderRadius: '50%',
                borderStyle: 'solid', borderWidth: '2px', borderColor: hRgba,
                background: handleBg,
            });
        } else { // minimal
            const size = this.orientation === 'vertical'
                ? { width: '6px', height: '40px' }
                : { width: '40px', height: '6px' };
            Object.assign(this.handle.style, {
                ...size, borderRadius: '3px',
                border: 'none',
                background: hRgba,
            });
        }
        this.config.handleStyle = style;
        // Recalculate line gap (handle size changed)
        this._updateLayout();
    }

    setEnableKeyboard(enabled: boolean) {
        // Always remove first to avoid duplicate listeners
        this.handle.removeEventListener('keydown', this._onKeyDown);
        if (enabled) {
            this.handle.addEventListener('keydown', this._onKeyDown);
        }
    }

    setEnableSnap(enabled: boolean) {
        this.handle.removeEventListener('dblclick', this._onDblClick);
        if (enabled) {
            this.handle.addEventListener('dblclick', this._onDblClick);
        }
    }

    show() {
        this.overlay.style.display = '';
    }

    hide() {
        this.overlay.style.display = 'none';
    }

    destroy() {
        this.destroyed = true;
        if (this.labelFadeTimeout) {
            clearTimeout(this.labelFadeTimeout);
        }
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this.handle.removeEventListener('pointerdown', this._onPointerDown);
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        this.handle.removeEventListener('keydown', this._onKeyDown);
        this.handle.removeEventListener('dblclick', this._onDblClick);
        this.overlay.remove();
    }
}
